import { FC, useMemo } from 'react';
import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { useLocale } from '@app/core/hooks/useLocale';
import { amountRules } from '@app/shared/components/cell-value/CellValueConfigurations';
import CellValue from '@app/shared/components/cell-value/CellValue';
import styles from '../../opm-inputs.module.scss';
import { ButtonAppearance, ButtonSize } from '@app/shared/components/button/button-enums';
import { Tooltip, TooltipPlacement } from '@app/shared/components/tooltip/Tooltip';
import {
  CalcMethod,
  InstrumentType,
  TooltipWidth,
} from '@app/shared/models/contracts/enums/shared-enums';
import Button from '@app/shared/components/button/Button';
import SvgInformation from '@app/shared/icons/Information';
import { Alignment } from '@app/shared/enums/alignment.enum';
import { enumKeyByValue } from '@app/shared/helpers';
import { dateFormatter } from '@app/shared/formatters';
import {
  OpmInputsInstrumentInformationDto,
  OpmInputsYearOrdinaryEquityInstrumentCalculationResultsDto,
} from '@app/shared/models/contracts/opm-calculation-results-dto';
import useLocalStorageCollapseState from '@app/core/hooks/useLocalStoreCollapse';
import SvgChevron from '@app/shared/icons/Chevron';
import { selectCapitalStructureId } from '@app/core/store/capital-structure-slice-selectors';
import {
  filterHistoricEventDatesFromFirstEvent,
  getMaxColumnCount,
} from '../../utils/OpmInputHelper';
import OpmInputHistoricCells from '../OpmInputHistoricCells';
import { Pill, PillSize, PillTheme } from '@app/shared/components/pill/Pill';

const OrdinaryEquityAdditionRedemptionSection: FC = (): JSX.Element | null => {
  const { l } = useLocale();
  const project = useAppSelector((state) => state.project?.projectDraft);
  const opmInputResults = useAppSelector((state) => state.opmCalculation.calculatedInputs);
  const opmInputs = project.opmInput;

  const [expanded, setExpanded] = useLocalStorageCollapseState(
    'expanded-opm-inputs-ordinary-equity',
    project.id
  );
  const showHistoricDataColumns = useAppSelector(
    (state) => state.uiValues.userSelections.opmInputs.showHistoricDataColumns
  );
  const historicColumnCount = Object.keys(
    filterHistoricEventDatesFromFirstEvent(opmInputResults?.historicEventDates ?? [])
  ).length;

  const toggleCollapse = () => {
    setExpanded(!expanded);
  };

  const hasInstrumentActivity = (
    instrumentData: OpmInputsYearOrdinaryEquityInstrumentCalculationResultsDto | undefined | null
  ) => {
    if (!instrumentData) {
      return false;
    }

    const periodHasAnyEvents = (instrumentData.periodByEvent || []).length > 0;

    return periodHasAnyEvents;
  };

  const ordinaryEquityInstruments: { [key: string]: OpmInputsInstrumentInformationDto } = useMemo(
    () =>
      Object.entries(opmInputResults.instrumentInformation?.instruments || {}).reduce(
        (acc, [instrumentId, instrument]) => {
          if (
            instrument.instrumentType !==
            enumKeyByValue(InstrumentType, InstrumentType.OrdinaryEquity)
          ) {
            return acc;
          }

          const allYears = [
            ...Object.values(opmInputResults.historicEventDates),
            ...Object.values(opmInputResults.forecastDates),
          ];

          const isInstrumentPresentAcrossYears = allYears.some((f) =>
            hasInstrumentActivity(f.ordinaryInstruments?.[instrumentId])
          );

          const isInstrumentFullyRedeemed = Object.values(allYears).some(
            (forecast) => !forecast.ordinaryInstruments?.[instrumentId]
          );

          const valuationDateOrdinaryInstrument =
            opmInputResults.valuationDate.ordinaryInstruments?.[instrumentId];

          const isInstrumentPresentOnValuationDate = hasInstrumentActivity(
            valuationDateOrdinaryInstrument
          );

          if (
            !isInstrumentPresentAcrossYears &&
            !isInstrumentPresentOnValuationDate &&
            !isInstrumentFullyRedeemed
          ) {
            return acc;
          }

          acc[instrumentId] = { ...instrument };
          return acc;
        },
        {} as { [key: string]: OpmInputsInstrumentInformationDto }
      ),
    [opmInputResults]
  );

  // determine if the project is an ERF project
  const buildStructures = useAppSelector((state) => state.capitalStructure.values.buildStructures);
  const capitalStructureId = useAppSelector(selectCapitalStructureId);
  const isErfBuildStructure = buildStructures?.[capitalStructureId].isErf;

  if (!isErfBuildStructure || Object.keys(ordinaryEquityInstruments).length === 0) {
    return null;
  }

  const numberOfInputForecastYears = Object.values(opmInputs.perYearInputs ?? {}).length;

  const caseForecasts = Object.entries(opmInputResults.forecastDates)
    .map(([forecastId, value]) => ({
      forecastId,
      ...value,
    }))
    .slice(0, numberOfInputForecastYears);

  const maxColspanCount =
    project.opmInput.forecastYears !== null
      ? getMaxColumnCount(
          historicColumnCount,
          project.opmInput.forecastYears,
          showHistoricDataColumns
        )
      : 0;
  const titleRowContentColSpan = maxColspanCount - 1;

  const renderTooltipContent = (
    instruments: Dictionary<OpmInputsYearOrdinaryEquityInstrumentCalculationResultsDto>,
    instrumentId: string
  ) => (
    <>
      <table>
        <thead>
          <tr className="table-primary__row--plain">
            <th>
              <strong>{l('_EventDateTitle')}</strong>
            </th>
            <th>
              <strong>{l('_InfusionOrBuybackNegative')}</strong>
            </th>
          </tr>
        </thead>
        <tbody>
          {(instruments[instrumentId]?.periodByEvent || []).map((event) => {
            if (event.additions === 0) {
              return null;
            }
            return (
              <tr className="table-primary--no-__row--plain" key={`${instrumentId}-${event.date}`}>
                <td>{dateFormatter(event.date.toString(), { dateStyle: 'short' })}</td>
                <td>
                  <CellValue alignment={Alignment.Right} {...amountRules} value={event.additions} />
                </td>
              </tr>
            );
          })}
          <tr>
            <th className="table-primary__cell--section-start-separator table-primary__cell--section-end-separator">
              <strong>{l('_Total')}</strong>
            </th>
            <td className="table-primary__cell--section-start-separator table-primary__cell--section-end-separator">
              <CellValue
                alignment={Alignment.Right}
                {...amountRules}
                value={instruments[instrumentId]?.periodTotal?.additions}
                strong
              />
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );

  const renderTableRowsCellContent = (
    instruments: Dictionary<OpmInputsYearOrdinaryEquityInstrumentCalculationResultsDto>,
    instrumentId: string
  ) => {
    const needsTooltip =
      instruments[instrumentId]?.periodByEvent.filter((event) => event.additions !== 0).length > 1;

    return (
      <>
        {instruments &&
          instrumentId in instruments &&
          hasInstrumentActivity(instruments[instrumentId]) && (
            <>
              <div style={{ display: 'flex', justifyContent: 'end' }}>
                <CellValue
                  {...amountRules}
                  alignment={Alignment.Right}
                  value={
                    instruments[instrumentId]?.periodTotal?.additions !== 0
                      ? instruments[instrumentId]?.periodTotal?.additions
                      : null
                  }
                  allowEmptyValues
                />
                {needsTooltip && (
                  <Tooltip
                    placement={TooltipPlacement.Top}
                    width={`${TooltipWidth.OrdinaryEquityDealThesisSection}px`}
                    content={renderTooltipContent(instruments, instrumentId)}>
                    <Button
                      className={styles['tooltip-button']}
                      startIcon={<SvgInformation />}
                      appearance={ButtonAppearance.CLEAN}
                      size={ButtonSize.SMALL}
                    />
                  </Tooltip>
                )}
              </div>
            </>
          )}
      </>
    );
  };

  const renderTableRowsCell = (
    instruments: Dictionary<OpmInputsYearOrdinaryEquityInstrumentCalculationResultsDto>,
    instrumentId: string,
    index?: number
  ) => {
    return (
      <td key={index !== undefined ? `${instrumentId}-${index}` : 'oe-valuation-total'}>
        {renderTableRowsCellContent(instruments, instrumentId)}
      </td>
    );
  };

  const calculateTotalAdditions = (
    instruments: Dictionary<OpmInputsYearOrdinaryEquityInstrumentCalculationResultsDto>
  ) => {
    return instruments
      ? Object.values(instruments).reduce((acc, value) => {
          if (value.periodTotal !== null && value.periodTotal.additions !== 0) {
            acc += value.periodTotal.additions;
          }
          return acc;
        }, 0)
      : null;
  };

  const renderTableTotalsCellContent = (totalAdditions: number | null) => (
    <CellValue
      {...amountRules}
      alignment={Alignment.Right}
      value={totalAdditions !== 0 ? totalAdditions : null}
      strong
      allowEmptyValues
    />
  );

  const renderTableTotalsCell = (totalAdditions: number | null, index?: number) => (
    <td
      className="table-primary__cell--section-start-separator table-primary__cell--section-end-separator"
      key={index !== undefined ? `${index}` : 'oe-valuation-total'}>
      {renderTableTotalsCellContent(totalAdditions)}
    </td>
  );

  return (
    <tbody>
      <tr data-testid="sd-title-section">
        <th className={`table-primary__cell--header-tertiary ${styles[`opm-header`]}`}>
          <div className={styles['opm-collapsable-header']}>
            <button
              type="button"
              aria-label={l('_ToggleSectionCollapse', {
                section: l('_OrdinaryEquityInstruments'),
              })}
              aria-expanded={expanded}
              style={{ border: 'none', backgroundColor: 'none', background: 'none' }}
              onClick={toggleCollapse}
              data-testid="sd-collapse-button">
              {!expanded ? (
                <SvgChevron onClick={toggleCollapse} className={styles[`opm-chevron-collapsed`]} />
              ) : (
                <SvgChevron onClick={toggleCollapse} className={styles[`opm-chevron-expanded`]} />
              )}
            </button>
            {l('_OrdinaryEquityInstruments')}
          </div>
        </th>
        <th
          className={`table-primary__cell--header-tertiary ${styles[`opm-header-badge`]}`}
          colSpan={titleRowContentColSpan}>
          {project.details.calcMethod !== CalcMethod.OPM && (
            <div className={styles['opm-collapsable-header-badge']} data-testid="sd-badge">
              <Pill size={PillSize.Xsmall} noTextTransform theme={PillTheme.White}>
                {l(`_Synced`)}
              </Pill>
            </div>
          )}
        </th>
      </tr>
      {expanded && (
        <>
          <tr className="table-primary__row--header">
            <th className="table-primary__cell--header-secondary table-primary__cell--vertical-separator table-primary__cell--section-start-separator table-primary__cell--section-end-separator">
              <strong>{l('_InfusionOrBuybackNegative')}</strong>
            </th>
            <td
              className="table-primary__cell--header-secondary table-primary__cell--vertical-separator table-primary__cell--section-start-separator table-primary__cell--section-end-separator"
              colSpan={titleRowContentColSpan}
            />
          </tr>
          {Object.entries(ordinaryEquityInstruments).map(([instrumentId, instrument]) => {
            return (
              <tr key={instrumentId}>
                <th className="table-primary__cell--vertical-separator">{instrument.narrative}</th>
                <OpmInputHistoricCells
                  indexIdentifier={`${instrumentId}-additions`}
                  renderCell={(_, historicYear) =>
                    renderTableRowsCellContent(historicYear.ordinaryInstruments, instrumentId)
                  }
                />
                {renderTableRowsCell(
                  opmInputResults.valuationDate.ordinaryInstruments,
                  instrumentId
                )}
                {caseForecasts.map((forecast, index) =>
                  renderTableRowsCell(forecast.ordinaryInstruments, instrumentId, index)
                )}
                <td />
              </tr>
            );
          })}
          <tr className="table-primary__row--plain">
            <th className="table-primary__cell--vertical-separator table-primary__cell--section-start-separator table-primary__cell--section-end-separator">
              <strong>{l('_Total')}</strong>
            </th>
            <OpmInputHistoricCells
              indexIdentifier="ordinary-equity-total-additions"
              className="table-primary__cell--section-start-separator table-primary__cell--section-end-separator"
              renderCell={(_, historicYear) => {
                const totalAdditions = calculateTotalAdditions(historicYear.ordinaryInstruments);
                return renderTableTotalsCellContent(totalAdditions);
              }}
            />
            {renderTableTotalsCell(
              calculateTotalAdditions(opmInputResults.valuationDate.ordinaryInstruments)
            )}
            {caseForecasts.map((forecast, index) => {
              const totalAdditions = calculateTotalAdditions(forecast.ordinaryInstruments);
              return renderTableTotalsCell(totalAdditions, index);
            })}
            <td className="table-primary__cell--section-start-separator table-primary__cell--section-end-separator" />
          </tr>
        </>
      )}
    </tbody>
  );
};

export default OrdinaryEquityAdditionRedemptionSection;
